<template>
  <UmisisModal ref="dialog" size="lg" title="Open a support request" :description="company ? `Tell us how we can help ${company.name}.` : 'Choose a company and tell us how we can help.'" hide-footer :no-close-on-backdrop="store.isSaving" :no-close-on-esc="store.isSaving" @show="initializeForm" @hide="invalidateLoad">
    <form class="flex w-full min-w-0 flex-col gap-4" @submit.prevent="createRequest">
      <Alert v-if="loadError || store.creationError" variant="destructive" role="alert">
        <AlertTitle>Unable to open support request</AlertTitle>
        <AlertDescription>{{ loadError || store.creationError }}</AlertDescription>
      </Alert>
      <p v-if="isLoading" role="status" class="text-sm text-muted-foreground">
        Loading request options…
      </p>
      <Button v-if="loadError" type="button" variant="outline" class="w-fit" @click="loadOptions">
        Retry
      </Button>
      <div class="flex min-w-0 flex-col gap-2">
        <label for="support-company" class="text-sm font-medium">Company</label>
        <select id="support-company" v-model="form.companyId" class="h-10 w-full min-w-0 max-w-full truncate rounded-md border bg-background px-3 text-sm disabled:opacity-50" required :disabled="company !== null || isLoading || store.isSaving">
          <option disabled value="">
            Select a company
          </option>
          <option v-for="option in companyOptions" :key="option.id" :value="option.id">
            {{ option.name }}
          </option>
        </select>
        <p v-if="!isLoading && !loadError && !companyOptions.length" class="text-sm text-muted-foreground">
          No companies are available for opening a support request.
        </p>
      </div>
      <div class="flex min-w-0 flex-col gap-2">
        <label for="support-subject" class="text-sm font-medium">Subject</label>
        <Input id="support-subject" v-model="form.subject" required maxlength="255" :disabled="store.isSaving" />
      </div>
      <div class="flex min-w-0 flex-col gap-2">
        <label for="support-category" class="text-sm font-medium">Category</label>
        <select id="support-category" v-model="form.category" class="h-10 w-full min-w-0 max-w-full truncate rounded-md border bg-background px-3 text-sm" required :disabled="isLoading || store.isSaving">
          <option disabled value="">
            Select a category
          </option>
          <option v-for="(label, value) in categories" :key="value" :value="value">
            {{ label }}
          </option>
        </select>
      </div>
      <div class="flex min-w-0 flex-col gap-2">
        <label for="support-description" class="text-sm font-medium">Describe the issue</label>
        <textarea id="support-description" v-model="form.description" class="min-h-32 rounded-md border bg-background px-3 py-2 text-sm" required maxlength="20000" :disabled="store.isSaving" />
      </div>
      <div class="flex justify-end gap-3">
        <Button type="button" variant="outline" :disabled="store.isSaving" @click="closeModal">
          Cancel
        </Button>
        <Button type="submit" :disabled="!canSubmit">
          {{ store.isSaving ? 'Opening…' : 'Open request' }}
        </Button>
      </div>
    </form>
  </UmisisModal>
</template>

<script setup>
import axios from 'axios'
import { computed, reactive, ref, watch } from 'vue'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import UmisisModal from '@/components/umisis/generic/UmisisModal.vue'
import { useModalControl } from '@/composables/modal/useUmisisModal.js'
import { useSupportRequestStore } from '../stores/supportRequestStore.js'

const props = defineProps({
  company: { type: Object, default: null },
})
const emit = defineEmits(['created'])
const { componentRef: dialog, openModal, closeModal } = useModalControl()
const store = useSupportRequestStore()
const form = reactive({ companyId: '', subject: '', category: '', description: '' })
const companies = ref([])
const categories = ref({})
const isLoading = ref(false)
const loadError = ref(null)
let loadId = 0

const companyOptions = computed(() => props.company ? [props.company] : companies.value)
const selectedCompany = computed(() => props.company ?? companies.value.find(company => company.id === form.companyId))
const canSubmit = computed(() => !isLoading.value && !loadError.value && !store.isSaving && !!selectedCompany.value?.id && !!form.subject.trim() && !!form.category && !!form.description.trim())

const invalidateLoad = () => { loadId++ }

const loadOptions = async () => {
  const requestId = ++loadId
  isLoading.value = true
  loadError.value = null

  try {
    const { data } = await axios.get('/api/v1/holidays-public/support-requests/options', { params: { company_uuid: props.company?.uuid } })
    if (requestId === loadId) {
      companies.value = data.data.companies
      categories.value = data.data.categories
    }
  } catch (error) {
    if (requestId === loadId) {
      loadError.value = error.response?.data?.message ?? 'Unable to load request options. Please try again.'
    }
  } finally {
    if (requestId === loadId) {
      isLoading.value = false
    }
  }
}

const initializeForm = () => {
  Object.assign(form, { companyId: props.company?.id ?? '', subject: '', category: '', description: '' })
  store.creationError = null
  companies.value = []
  categories.value = {}
  return loadOptions()
}

const createRequest = async () => {
  if (!canSubmit.value) {
    return
  }

  const created = await store.createRequest(form, selectedCompany.value)
  if (created) {
    closeModal()
    emit('created', created)
  }
}

watch(() => props.company, company => {
  form.companyId = company?.id ?? ''
})

defineExpose({ openModal, closeModal })
</script>
